import {
    Injectable, inject
} from "@angular/core";
import {
    Observable, of, catchError
} from "rxjs";
import { map } from "rxjs/operators";
import { StringUtil } from "@shopliftr/common-js/data";
import {
    Product, ProductSearchFilterOptions
} from "@shopliftr/common-js/shared";
import { IAutocompleteController } from "../../interfaces/autocomplete-controller";
import { IAutocompleteResults } from "../../interfaces/autocomplete-results";
import { IAutocompleteOption } from "../../interfaces/autocomplete-option";
import { ProductApiClientService } from "../../iapi-application/services/api-client/product/product-api-client.service";

/**
 * Autocomplete controller service for product selection.
 *
 * Implements {@link IAutocompleteController} for {@link Product} to provide
 * autocomplete functionality backed by the v2 product search endpoint via
 * {@link ProductApiClientService}. Only a single page of results (capped at
 * {@link ProductSearchAutocompleteControllerService._MAX_RESULTS}, starting at offset 0) is requested per keystroke,
 * and only once the query has at least {@link ProductSearchAutocompleteControllerService._MIN_QUERY_LENGTH} characters.
 * The display label is constructed from the product's UPC, brand, name, and description fields.
 *
 * @example
 * // Wire up to an autocomplete input via the parent component
 * autocompleteController = productSearchController; // injected ProductSearchAutocompleteControllerService
 * products$ = this.autocompleteController.autocomplete$("Granola");
 */
@Injectable({
    providedIn: "root"
})
export class ProductSearchAutocompleteControllerService implements IAutocompleteController<Product> {
    /**
     * Maximum number of products requested (a single page) per autocomplete query.
     */
    private static readonly _MAX_RESULTS = 20;

    /**
     * Minimum length (after trimming) a query must have before the API is called.
     */
    private static readonly _MIN_QUERY_LENGTH = 2;

    private readonly _productClient = inject(ProductApiClientService);

    /**
     * Gets autocomplete results for products based on the provided keyword query.
     *
     * A query shorter than {@link ProductSearchAutocompleteControllerService._MIN_QUERY_LENGTH} characters
     * after sanitizing (trimming), including empty or blank queries, returns an empty result set
     * immediately without calling the API. Longer queries are forwarded to
     * {@link ProductApiClientService.search$} as a keyword filter, requesting
     * only the first page of at most {@link ProductSearchAutocompleteControllerService._MAX_RESULTS}
     * results (no paging through the full result set).
     * API errors are caught and an empty result set is returned.
     *
     * @param query - The keyword search string to filter products
     * @returns Observable of autocomplete results containing {@link Product} values
     */
    autocomplete$(query?: string): Observable<IAutocompleteResults<Product>> {
        const keyword = StringUtil.emptyString(query) ? "" : StringUtil.sanitizeString(query);

        if (keyword.length < ProductSearchAutocompleteControllerService._MIN_QUERY_LENGTH) {
            return of({
                options: []
            });
        }

        const filterOptions = new ProductSearchFilterOptions();
        filterOptions.keyword = keyword;

        return this._productClient.search$(filterOptions, 0, ProductSearchAutocompleteControllerService._MAX_RESULTS).pipe(
            map((response) => {
                const autocompleteOptions: Array<IAutocompleteOption<Product>> = response.results.map((product) => ({
                    option: this.getAutocompleteDisplayName(product),
                    value: product
                }));

                return {
                    options: autocompleteOptions
                };
            }),
            catchError(() => {
                return of({
                    options: []
                });
            })
        );
    }

    /**
     * Builds the display label for a product in the autocomplete list.
     *
     * The label has the format `"<upc> - <parts>"` where parts are the
     * space-joined non-empty values of brand, name, and description.
     * If the product is undefined, an empty string is returned.
     * If all optional parts are empty, only the UPC is returned.
     *
     * @example
     * // All fields present → "012345678905 - Acme Granola 12ct"
     * // Brand + name only  → "012345678905 - Acme Granola"
     * // Name only          → "012345678905 - Granola"
     * // UPC only           → "012345678905"
     *
     * @param product - The product value to build a label for
     * @returns The human-readable display name for the product
     */
    getAutocompleteDisplayName(product?: Product): string {
        if (!product) {
            return "";
        }

        const parts = [product.brand, product.name, product.description]
            .filter((part) => !StringUtil.emptyString(part));

        return parts.length ? `${product.upc} - ${parts.join(" ")}` : product.upc;
    }

    /**
     * Gets the unique identifier for tracking purposes in autocomplete lists.
     * Uses the product's UPC as the stable identifier.
     *
     * @param index - The index of the item in the list
     * @param result - The autocomplete option result
     * @returns The UPC string of the product, or undefined if unavailable
     */
    trackByIdentifier(index: number, result?: IAutocompleteOption<Product>): string | undefined {
        return result?.value?.upc;
    }

    /**
     * Determines whether the given product is unique within the items array.
     * Uniqueness is determined by UPC comparison.
     *
     * @param product - The product to check
     * @param items - Array of existing products
     * @returns `true` if no item in the array has the same UPC, `false` otherwise
     */
    isUnique(product: Product, items: Array<Product>): boolean {
        return !items?.some((p) => p.upc === product.upc);
    }
}
